import { useIntl } from '@umijs/max';
import { App, Descriptions, Input, Modal, Radio, Spin } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { TaskReportFlowAction } from '@/constants/flowAction';
import {
  getFlowRecords,
  processFlowWithComment,
} from '@/services/ant-design-pro/api';
import type { FlowRecordDto } from '@/services/ant-design-pro/typings';
import './RedoApprovalModal.less';

export type RedoApprovalReportRecord = {
  taskId?: string;
  taskName?: string;
  electronicReportCode?: string;
  redoReason?: string;
  comment?: string;
};

type RedoApprovalModalProps = {
  open: boolean;
  record: RedoApprovalReportRecord | null;
  onCancel: () => void;
  onSuccess: () => void;
  /** 嵌套在其他 Modal 内打开时需提高层级 */
  zIndex?: number;
};

function pickRedoReasonFromRecords(
  records: FlowRecordDto[] | undefined,
): string | undefined {
  if (!records?.length) return undefined;
  for (let i = records.length - 1; i >= 0; i -= 1) {
    const comments = records[i]?.comments;
    if (Array.isArray(comments) && comments.length > 0) {
      const text = comments.filter(Boolean).join('\n').trim();
      if (text) return text;
    }
  }
  return undefined;
}

const RedoApprovalModal: React.FC<RedoApprovalModalProps> = ({
  open,
  record,
  onCancel,
  onSuccess,
  zIndex,
}) => {
  const intl = useIntl();
  const { message: messageApi } = App.useApp();
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<'pass' | 'reject'>('pass');
  const [approvalComment, setApprovalComment] = useState('');
  const [redoReason, setRedoReason] = useState('');

  useEffect(() => {
    if (!open || !record?.taskId) return;

    const fromList = (record.redoReason || record.comment || '').trim();
    if (fromList) {
      setRedoReason(fromList);
      return;
    }

    let mounted = true;
    (async () => {
      setLoadingDetail(true);
      try {
        const resp = await getFlowRecords({ taskId: record.taskId! });
        if (!mounted) return;
        if (resp?.success && resp?.result) {
          const reason = pickRedoReasonFromRecords(resp.result);
          if (reason) setRedoReason(reason);
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (mounted) setLoadingDetail(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [open, record]);

  useEffect(() => {
    if (open) {
      setResult('pass');
      setApprovalComment('');
    }
  }, [open]);

  const titleSuffix = useMemo(() => {
    if (!record) return '';
    return record.taskName || record.electronicReportCode
      ? ` - ${record.taskName || record.electronicReportCode}`
      : '';
  }, [record]);

  const handleSubmit = async () => {
    if (!record?.taskId) {
      messageApi.error(
        intl.formatMessage({ id: 'pages.report.redo.noTaskId' }),
      );
      return;
    }
    if (result === 'reject' && !approvalComment.trim()) {
      messageApi.warning(
        intl.formatMessage({ id: 'pages.report.redo.rejectCommentRequired' }),
      );
      return;
    }

    const action =
      result === 'pass'
        ? TaskReportFlowAction.REDO_APPROVE_PASS
        : TaskReportFlowAction.REDO_APPROVE_REJECT;

    setSubmitting(true);
    try {
      const resp = await processFlowWithComment({
        taskId: record.taskId,
        action,
        comment: approvalComment.trim(),
      });
      if (resp?.success) {
        messageApi.success(
          result === 'pass'
            ? intl.formatMessage({ id: 'pages.report.redo.approvePassSuccess' })
            : intl.formatMessage({
                id: 'pages.report.redo.approveRejectSuccess',
              }),
        );
        onSuccess();
        onCancel();
      } else {
        messageApi.error(
          resp?.message ||
            intl.formatMessage({ id: 'pages.report.redo.approveFail' }),
        );
      }
    } catch (e: any) {
      messageApi.error(
        e?.message ||
          intl.formatMessage({ id: 'pages.report.redo.approveFail' }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      className="redo-approval-modal"
      open={open}
      title={`${intl.formatMessage({ id: 'pages.report.redo.approveTitle' })}${titleSuffix}`}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={submitting}
      destroyOnClose
      width={560}
      zIndex={zIndex}
      okText={intl.formatMessage({ id: 'pages.common.submit' })}
      cancelText={intl.formatMessage({ id: 'pages.common.cancel' })}
    >
      <Spin spinning={loadingDetail}>
        <Descriptions
          column={1}
          bordered
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Descriptions.Item
            label={intl.formatMessage({ id: 'pages.report.redo.reasonLabel' })}
          >
            {redoReason || '-'}
          </Descriptions.Item>
        </Descriptions>

        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>
            {intl.formatMessage({ id: 'pages.report.redo.approveResultLabel' })}
          </div>
          <Radio.Group
            value={result}
            onChange={(e) => setResult(e.target.value)}
            options={[
              {
                label: intl.formatMessage({ id: 'pages.report.redo.pass' }),
                value: 'pass',
              },
              {
                label: intl.formatMessage({ id: 'pages.report.redo.reject' }),
                value: 'reject',
              },
            ]}
          />
        </div>

        <div className="redo-approval-comment">
          <div style={{ marginBottom: 8, fontWeight: 500 }}>
            {intl.formatMessage({
              id: 'pages.report.redo.approveCommentLabel',
            })}
            {result === 'reject' && (
              <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>
            )}
          </div>
          <Input.TextArea
            value={approvalComment}
            onChange={(e) => setApprovalComment(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'pages.report.redo.approveCommentPlaceholder',
            })}
            autoSize={{ minRows: 2, maxRows: 4 }}
            maxLength={200}
            showCount
            styles={{
              textarea: { paddingBottom: 22 },
              count: { bottom: 4, insetInlineEnd: 11 },
            }}
          />
        </div>
      </Spin>
    </Modal>
  );
};

export default RedoApprovalModal;
